import { Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { NavigationFlowService } from 'app/services/navigation-flow.service';

export interface DialogSize {
  width: number;
  height: number;
}

export interface DialogCloseContext {
  dialogType?: 'otp-suggest-success' | 'otp-changepwd' | 'default';
  navigationFlow?: NavigationFlowService;
}

@Injectable({
  providedIn: 'root'
})
export class DialogUtilsService {

  constructor() { }

  /**
   * Calculates the optimal dialog size based on screen dimensions
   * @returns DialogSize object containing width and height
   */
  calculateDialogSize(): DialogSize {
    let screenWidth = window.innerWidth;
    let screenHeight = window.innerHeight;
    let dialogWidth: number;

    if (screenWidth <= 600) {
      // Small screen: 4 columns + 3 gutters (8px)
      const totalGutters = 3;
      const gutterWidth = 8;
      const columnWidth = (screenWidth - totalGutters * gutterWidth) / 4;
      dialogWidth = (columnWidth * 4) + (3 * gutterWidth);
    } else if (screenWidth <= 1023) {
      // Medium screen: 8 columns + 9 gutters (16px)
      const totalGutters = 9;
      const gutterWidth = 16;
      const columnWidth = (screenWidth - totalGutters * gutterWidth) / 8;
      dialogWidth = (columnWidth * 6) + (5 * gutterWidth); // Center 6 columns
    } else {
      // Large screen: 12 columns (88px) + 13 gutters (16px)
      const columnWidth = 88;
      const gutterWidth = 16;
      dialogWidth = (6 * columnWidth) + (5 * gutterWidth); // Center 6 columns
    }

    // Make sure dialog width doesn't exceed viewport
    dialogWidth = Math.min(dialogWidth, screenWidth * 0.95);

    // Calculate height
    const dialogHeight = Math.min(screenHeight * 0.8, 552);

    return { width: dialogWidth, height: dialogHeight };
  }

  /**
   * Applies vertical positioning to a dialog container
   * @param container The dialog container element
   */
  applyVerticalPositioning(container?: HTMLElement): void {
    if (!container) {
      container = document.querySelector('.mat-dialog-container') as HTMLElement;
      if (!container) return;
    }

    const containerHeight = container.getBoundingClientRect().height;
    const viewportHeight = window.innerHeight;

    if (containerHeight + 160 > viewportHeight) {
      container.style.top = '80px';
      container.style.margin = '0 auto';
    } else {
      container.style.top = '';
      container.style.margin = '';
    }
  }

  /**
   * Sets up responsive resizing for a dialog
   * @param dialogRef Reference to the MatDialogRef
   * @returns A function that should be called to clean up event listeners
   */
  setupResponsiveDialog(dialogRef: any): () => void {
    const resizeHandler = () => {
      if (!dialogRef) return;

      const { width, height } = this.calculateDialogSize();
      dialogRef.updateSize(`${width}px`, `${height}px`);
      this.applyVerticalPositioning();
    };

    // Set initial positioning
    setTimeout(() => this.applyVerticalPositioning(), 0);

    // Add resize listener
    window.addEventListener('resize', resizeHandler);

    // Return cleanup function
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }

  /**
   * Determines if close button should be visible
   * Returns true for both v1.5 and v2.0 - button always visible
   */
  shouldShowCloseButton(): boolean {
    return true;
  }

  closeDialog(dialogRef: MatDialogRef<any>, result?: any, context?: DialogCloseContext): void {
    console.log('[DIALOG-UTILS] closeDialog called', { context });
    
    // Always close the dialog first
    dialogRef.close(result);

    // Handle iframe mode
    const isInIframe = window.self !== window.top;
    console.log('[DIALOG-UTILS] isInIframe:', isInIframe);
    
    if (isInIframe) {
      this.handleIframeClose();
      return;
    }

    // Check API version
    let isV2 = false;
    try {
      const sessionData = localStorage.getItem('ferrerSessionData');
      if (sessionData) {
        const parsedData = JSON.parse(sessionData);
        isV2 = parsedData?.apiVersion === '2.0';
      }
    } catch {
      isV2 = false;
    }

    console.log('[DIALOG-UTILS] API Version:', isV2 ? '2.0' : '1.5');

    // v2.0 behavior
    if (isV2) {
      // Special handling for OTP success dialogs
      const dialogType = context?.dialogType || 'default';
      
      if (dialogType === 'otp-suggest-success' || dialogType === 'otp-changepwd') {
        console.log('[DIALOG-UTILS] OTP success dialog closed, opening login');
        
        if (context?.navigationFlow) {
          // Use NavigationFlowService to open login dialog
          setTimeout(() => {
            context.navigationFlow!.openLoginDialog().subscribe({
              next: () => console.log('[DIALOG-UTILS] Login dialog opened'),
              error: (err) => console.error('[DIALOG-UTILS] Error opening login dialog:', err)
            });
          }, 300); // Small delay for smooth transition
        } else {
          console.warn('[DIALOG-UTILS] NavigationFlowService not provided for OTP success dialog');
        }
      } else {
        // Default v2.0: Just close, stay on page
        console.log('[DIALOG-UTILS] v2.0 default: staying on current page');
      }
      return;
    }

    // v1.5 behavior: Always navigate to returnUrl
    console.log('[DIALOG-UTILS] v1.5: navigating to returnUrl');
    this.navigateToReturnUrl();
  }

  private handleIframeClose(): void {
    console.log('[DIALOG-UTILS] In iframe mode, checking for closeAllDialogs method');
    console.log('[DIALOG-UTILS] typeof closeAllDialogs:', typeof (window as any).closeAllDialogs);
    
    // Use the global method that properly handles iframe closure
    if (typeof (window as any).closeAllDialogs === 'function') {
      console.log('[DIALOG-UTILS] Calling closeAllDialogs()');
      (window as any).closeAllDialogs();
    } else {
      console.warn('[DIALOG-UTILS] closeAllDialogs not available, using fallback');
      // Fallback: notify parent to close with structured message
      if (window.parent && window.parent !== window) {
        try {
          console.log('[DIALOG-UTILS] Sending closeDialog message to parent');
          // Send structured message (matches app.component.ts pattern)
          window.parent.postMessage({
            type: 'closeDialog',
            action: 'close'
          }, '*');
          
          // Keep legacy string messages for backwards compatibility
          window.parent.postMessage('closeDialog', '*');
          window.parent.postMessage('loginDialogClosed', '*');
          window.parent.postMessage('dialogClosed', '*');
          window.parent.postMessage('stopRetry', '*');
        } catch (error) {
          console.error('[DIALOG-UTILS] Error notifying parent window:', error);
        }
      }
    }
  }

  private navigateToReturnUrl(): void {
    try {
      // Priority 1: Use callback URL from localStorage if available
      const callback = localStorage.getItem('ferrer');
      if (callback) {
        console.log('[DIALOG-UTILS] Navigating to callback:', callback);
        window.location.href = callback;
        return;
      }

      // Priority 2: Use siteUrl from sessionData
      const sessionData = localStorage.getItem('ferrerSessionData');
      if (sessionData) {
        const parsedData = JSON.parse(sessionData);
        if (parsedData?.siteUrl) {
          console.log('[DIALOG-UTILS] Navigating to siteUrl:', parsedData.siteUrl);
          window.location.href = parsedData.siteUrl;
          return;
        }
      }

      // Priority 3: Use referrer if available
      if (document.referrer) {
        console.log('[DIALOG-UTILS] Navigating to referrer:', document.referrer);
        window.location.href = document.referrer;
        return;
      }

      // Priority 4: Fall back to history.back() if there's history
      if (window.history.length > 1) {
        console.log('[DIALOG-UTILS] Using history.back()');
        window.history.back();
      }
    } catch (error) {
      console.error('[DIALOG-UTILS] Navigation error:', error);
    }
  }
}
